import React from 'react';
import styled from 'styled-components';

import DateTimePicker from 'Components/DateTmePicker';
import Button from 'Components/Button';
import Loader from 'Components/Loader';
import Modal from 'Components/BottomModal';
import dayjs from 'dayjs';
import useI18n from 'i18n';
import getLocale from 'i18n/getLocale';
import { useShoppingCart, useShoppingCartApi } from 'Components/ShoppingCartUniverse';
import { useBridgeApi } from 'Hooks';
import { ModalType } from '..';
import AdyenDropIn from '../Components/AdyenDropIn';
import OpacityTouchable from 'Components/OpacityTouchable';

const ModalText = styled.p`
  text-align: center;
  font-size: 18px;
  font-family: 'Eina01';
  font-weight: normal;
  margin-top: 10px;
  margin-bottom: 16px;
`;

const FailureWrapper = styled.div`
  padding: 24px;
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
  justify-content: space-evenly;
  align-items: center;
  height: 40%;
  margin-bottom: 16px;
`;

const SuccessWrapper = styled.div`
  padding: 80px;
  margin-bottom: 20px;
  display: flex;
  box-sizing: border-box;
  height: calc(var(--height) - 220px);
  justify-content: center;
  align-items: center;
  flex-direction: column;
`;

interface Props {
  /**
   * mode
   */
  mode: string;
  /**
   * error
   */
  error: string | null;
  /**
   * sessionData
   */
  sessionData: any;
  /**
   * setMode
   */
  setMode: (mode: ModalType) => void;
  /**
   * setTime
   */
  setTime: (time: any) => void;
  /**
   * time
   */
  time: string;
  /**
   * setError
   */
  setError: React.Dispatch<React.SetStateAction<string>>;
  /**
   * getSession
   */
  getSession: () => Promise<void>;
  /**
   * setPayload
   */
  setPayload: React.Dispatch<React.SetStateAction<string>>;
  /**
   * updateDetails
   */
  updateDetails: () => Promise<void>;
  /**
   * loading
   */
  loading: boolean;
  /**
   * onInvoicePayment
   */
  onInvoicePayment: () => void;
}

/**
 * ModalContent component
 */

const ModalContent: React.FC<Props> = ({
  setTime,
  time,
  sessionData,
  mode,
  setMode,
  error,
  setError,
  getSession,
  setPayload,
  updateDetails,
  loading,
  onInvoicePayment,
}) => {
  const { i18n } = useI18n();
  const shoppingCart = useShoppingCart();
  const shoppingCartApi = useShoppingCartApi();
  const api = useBridgeApi();
  const locale = getLocale();

  const commentSubmit = (value: string) => {
    // Replace emojis from comment field with an empty space.
    const filteredComment = value.replace(
      /(\u00a9|\u00ae|[\u2000-\u3300]|\ud83c[\ud000-\udfff]|\ud83d[\ud000-\udfff]|\ud83e[\ud000-\udfff])/g,
      ' ',
    );
    setMode(ModalType.Hide);
    shoppingCartApi.setComment(filteredComment);
    api.vibrate('impactLight');
  };

  switch (mode) {
    case 'DesiredTime':
      return (
        <Modal
          title={i18n('Date.PickADate')}
          buttonText={i18n('Date.PickADate.OK')}
          onClose={() => {
            setMode(ModalType.Hide);
          }}
          desktopMaxWidth={400}>
          <DateTimePicker
            time={time}
            onSubmit={(val) => {
              shoppingCartApi.setPickupTime(dayjs(val).toISOString());
              setMode(ModalType.Hide);
              setTime(dayjs(val).toISOString());
              api.vibrate('impactLight');
            }}
          />
        </Modal>
      );

    case 'Comment':
      return (
        <Modal
          title={i18n('Cart.Modal.SendComment')}
          buttonText={i18n('Cart.Modal.SendComment.Button')}
          placeholder={i18n('Cart.Modal.SendComment.Placeholder')}
          value={shoppingCart.comment ?? ''}
          textarea
          onSubmit={(val) => commentSubmit(val)}
          onClose={() => {
            setMode(ModalType.Hide);
          }}
          desktopMaxWidth={500}
          disableButtonTransform></Modal>
      );

    // case 'Discount':
    //   return (
    //     <Modal
    //       title={i18n('Cart.Modal.DiscountCode.Title')}
    //       buttonText={i18n('Cart.Modal.DiscountCode.Button')}
    //       placeholder={i18n('Cart.Modal.DiscountCode.Placeholder')}
    //       value={discountCode}
    //       onSubmit={(val) => {
    //         setMode(ModalType.Hide);
    //         setDiscountCode(val);
    //       }}
    //       onClose={() => {
    //         setMode(ModalType.Hide);
    //         setDiscountCode('');
    //       }}></Modal>
    //   );

    case 'ErrorASD':
      return (
        <Modal title={i18n('Cart.Modal.Error.Title')} onClose={() => {}} disableClose={true}>
          <FailureWrapper>
            <ModalText>{i18n('Cart.Modal.Error.Text')}</ModalText>
            <OpacityTouchable>
              <Button
                disabled={loading}
                width="200px"
                text={i18n('Cart.Modal.Error.Button')}
                handleClick={() => {
                  updateDetails();
                }}
              />
            </OpacityTouchable>
          </FailureWrapper>
        </Modal>
      );

    case 'PaymentComplete':
      return (
        <Modal
          baeLogo
          title={
            shoppingCart.isFullyPaid
              ? i18n('Cart.Modal.PaymentComplete.Title.Second')
              : i18n('Cart.Modal.PaymentComplete.Title.First')
          }
          onClose={() => {
            setMode(ModalType.Hide);
          }}
          disableClose={true}>
          <SuccessWrapper>
            <Loader color="#757578" />
            <ModalText>
              {shoppingCart.isFullyPaid
                ? i18n('Cart.Modal.PaymentComplete.Text.Second')
                : i18n('Cart.Modal.PaymentComplete.Text.First')}
            </ModalText>
          </SuccessWrapper>
        </Modal>
      );

    case 'AfterPaymentError':
      return (
        <Modal
          title={i18n('Cart.Modal.PaymentError.Title')}
          onClose={() => setMode(ModalType.Hide)}>
          <FailureWrapper>
            <ModalText>{i18n('Error.HANDLE_ORDER_AFTER_PAYMENT_ERROR')}</ModalText>
            <OpacityTouchable>
              <Button
                text={i18n('Cart.Modal.PaymentError.Button')}
                width="200px"
                handleClick={() => {
                  api.vibrate('impactMedium');
                  getSession();
                  setMode(ModalType.Hide);
                  setError('');
                }}
              />
            </OpacityTouchable>
          </FailureWrapper>
        </Modal>
      );

    case 'AdyenDropIn':
      return (
        <Modal
          baeLogo
          disableClose={false}
          title={i18n('Cart.Modal.Payment.Title')}
          large
          hidden={false}
          onClose={() => {
            setMode(ModalType.Hide);
          }}>
          <AdyenDropIn
            key="adyen"
            sessionData={sessionData}
            error={null}
            setShowModal={(m: any) => setMode(m)}
            setPayload={setPayload}
            updateDetails={updateDetails}
            locale={locale}
            onInvoicePayment={onInvoicePayment}
          />
        </Modal>
      );
    case 'nope':
      return (
        <Modal
          baeLogo
          disableClose={false}
          title={i18n('Cart.Modal.Payment.Title')}
          large
          onClose={() => {
            setMode(ModalType.Hide);
          }}>
          <AdyenDropIn
            key="adyen"
            sessionData={sessionData}
            error={i18n('Cart.Modal.AfterPaymentError.Text')}
            setShowModal={(m: any) => setMode(m)}
            setPayload={setPayload}
            updateDetails={updateDetails}
            locale={locale}
            onInvoicePayment={onInvoicePayment}
          />
        </Modal>
      );

    case 'Error':
    case 'PaymentRefused':
    case 'SessionError':
    case 'PaymentError':
      return (
        <Modal
          baeLogo
          disableClose={false}
          title={i18n('Cart.Modal.Payment.Title')}
          large
          onClose={() => {
            setMode(ModalType.Hide);
          }}>
          <AdyenDropIn
            key="adyen"
            sessionData={sessionData}
            error={i18n(error && error.length > 0 ? error : 'Cart.Modal.PaymentError.Text')}
            setShowModal={(m: any) => setMode(m)}
            setPayload={setPayload}
            updateDetails={updateDetails}
            locale={locale}
            onInvoicePayment={onInvoicePayment}
          />
        </Modal>
      );

    default:
      return null;
  }
};

export default ModalContent;
